import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { AppThemeContext } from "../../contexts/colors";
import { localeContext } from "../../contexts/localeManagement";
import { AuthContext } from "../../contexts/accountManagement"
import SectionList from "../../Components/Custom/SectionList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SettingsItem from "../../Components/Utilities/SettingsItem";
import { faCircleInfo, faGlobe, faMoon, faPaintRoller, faSun, faUser } from "@fortawesome/free-solid-svg-icons";
import MaterialCommunityIcons from "../../Components/Custom/MaterialCommunityIcons";
import BottomPopupModal from "../../Components/Utilities/BottomPopupModal";
import LanguageItem from "../../Components/Utilities/LanguageItem";

export default function PreferencesScreen() {
	const {
		dark,
		colors,
		setRuntimeTheme,
		theme
	} = useContext(AppThemeContext);
	const { logOut } = useContext(AuthContext);
	const { locale, appText, setApplicationLocale } = useContext(localeContext);

	//* States and Refs
	const [languageModalVis, setLanguageModalVis] = useState(false);
	const bottomModalRef = useRef();

	const prefStyles = useMemo(() => ({
		container: {
			display: "flex",
			flex: 1,
			flexDirection: "column",
			backgroundColor: colors().screen,
			justifyContent: "center",
			alignContent: "center",
			height: "100vh"
		},
		sectionTitle: {
			color: colors().gray,
			fontSize: 24,
			fontWeight: "bold",
			marginVertical: 5,
			[locale === "ar" ? "marginRight" : "marginLeft"]: 8,
			fontFamily: "Courier New"
		}
	}), [dark, locale]);

	const data = useMemo(() => [
		{
			title: appText["Information"],
			data: [
				{
					icon: "file-document",
					content: "Instructions",
					color: dark ? colors().gray.replace(/5b/g, "6f") : colors().gray,
					onPress: () => alert("under construction"),
					fontawesome: false,

					guideText: "This is the settings screen, you can edit different kinds of settings here. This button will show information about the app (under construction)",
					guideName: "setIntro"
				},
				{
					icon: faCircleInfo,
					content: "SettingsCredits",
					color: dark ? colors().success : colors().lightButton,
					onPress: () => {},//navigation.navigate("Credits"),
					fontawesome: true,

					guideText: "This shows you a list of the people who contributed towards the making of this app",
					guideName: "credInfo"
				}
			],
			icon: "apple-keyboard-command",
			fontawesome: false,
			iconSize: null
		},
		{
			title: appText["Personalization"],
			data: [
				{
					icon: faGlobe,
					content: "Language",
					color: dark ? colors().tertiary : colors().success,
					onPress: () => setLanguageModalVis(true),
					fontawesome: true,
					secondaryText: `(${(() => {
						let langText = "";

						switch (locale) {
							case "ar":
								langText = "العربية"
								break;

							case "en":
								langText = "English"
								break;

							case "de":
								langText = "Deutsch"
								break;

							case "fr":
								langText = "Français"
								break;

							default:
								langText = "العربية"
								break;
						}
						return langText;
					})()})`,

					guideText: "Use this to select your preferred language",
					guideName: "langInfo"
				},
				{
					icon: theme === "dark" ? faMoon : faSun,
					content: `Theme`,
					color: dark ? colors().listItem : colors().tertiary,
					onPress: (() => {
						let finalFunc = async () => { alert("Something went wrong.") };
						switch (theme) {
							case "dark":
								finalFunc = async () => {
									await localStorage.setItem("theme", "light");
									await setRuntimeTheme();
								}
								break;

							case "light":
								finalFunc = async () => {
									await localStorage.setItem("theme", "automatic");
									await setRuntimeTheme();
								}
								break;

							case "automatic":
								finalFunc = async () => {
									await localStorage.setItem("theme", "dark");
									await setRuntimeTheme();
								}
								break;

							default:
								break;
						}
						return finalFunc;
					})(),
					fontawesome: true,
					secondaryText: `(${theme.slice(0, 1).toUpperCase() + theme.slice(1)})`,
					// iconSize: theme === "automatic" ? 20 : null,

					guideText: "Use this to select your preferred theme (Dark, Light, or Automatic based on your device settings)",
					guideName: "themeInfo"
				}
			],
			icon: faPaintRoller,
			fontawesome: true
		},
		{
			title: appText["Account"],
			data: [
				{
					icon: "logout",
					content: "Log Out",
					color: colors().danger,
					onPress: logOut,

					guideText: "Use this to log out of your account any time you feel like it!",
					guideName: "logOutInfo"
				}
			],
			icon: faUser,
			fontawesome: true
		}
	], [dark, theme, locale]);

	const renderItems = useCallback(({ item, index, section }) => {
		// const sectionIndex = data.findIndex(sec => sec.title === section.title);
		// const globalIndex = getGlobalIndex(data, sectionIndex, index);

		return (
			// <CopilotStep text={appText[item.guideText]} order={globalIndex + 1} name={item.guideName}>
				<SettingsItem
					icon={item.icon}
					content={item.content}
					color={item.color}
					onPress={item.onPress}
					IconComponent={item.fontawesome ? FontAwesomeIcon : null}
					secondaryText={item.secondaryText ? item.secondaryText : null}
					disabled={item.disabled ? item.disabled : false}
				/>
			// </CopilotStep>
		)
	}, [data, locale]);

	const renderSectionHeader = useCallback(({ section: { title, icon, fontawesome } }) => (
		<div style={{ display: "flex", flexDirection: locale === "ar" ? "row-reverse" : "row", alignItems: "center" }}>
			{fontawesome
				? <FontAwesomeIcon icon={icon} size="2x" color={colors().gray} style={{ [locale === "ar" ? "marginRight" : "marginLeft"]: 5, marginTop: 2 }} />
				: <MaterialCommunityIcons name={icon} size={22} color={colors().gray} style={{ [locale === "ar" ? "marginRight" : "marginLeft"]: 5, marginTop: 2 }} />}
			<h1 style={prefStyles.sectionTitle}>
				{title}
			</h1>
		</div>
	), [data, locale])

	return (
		<>
			<div style={prefStyles.container}>
				<SectionList
					sections={data}
					renderItem={renderItems}
					keyExtractor={(item) => item.content}
					renderSectionHeader={renderSectionHeader}
					contentContainerStyle={{ paddingTop: 122 }}
				/>
				<BottomPopupModal
					modalTitle="Choose Language"
					modalVisible={languageModalVis}
					setModalVisible={setLanguageModalVis}
					ref={bottomModalRef}
				>
					<LanguageItem
						language="ar"
						onPress={() => {
							setApplicationLocale("ar");
							bottomModalRef.current.cancelModal();
						}}
					/>
					<LanguageItem
						language="en"
						onPress={() => {
							setApplicationLocale("en");
							bottomModalRef.current.cancelModal();
						}}
					/>
					<LanguageItem
						language="de"
						onPress={() => {
							setApplicationLocale("de");
							bottomModalRef.current.cancelModal();
						}}
					/>
					<LanguageItem
						language="fr"
						onPress={() => {
							setApplicationLocale("fr");
							bottomModalRef.current.cancelModal();
						}}
					/>
					{/* <h1>test</h1> */}
				</BottomPopupModal>
			</div>
		</>
	);
}